<script setup lang="ts">
import type { Components } from '@ionic/core'
import type { AssociationMembershipResponse } from '~~/openapi-client/gen/schemas'
import { openModal } from '~/utils/openModal'
import { EditMemberModal } from '#components'
import InviteSuccess from '~/pages/modals/invite/success.modal.vue'

const props = defineProps<{
	nav: ComponentPublicInstance<Components.IonNav>
}>()

const nav = props.nav

const {
	selectedMembers,
	toggleSelected,
	inviteMembers,
	refresh,
	hasEmail,
	isSystemUser,
} = useMembers()

const allMembersHaveEmail = computed(() => selectedMembers.value.every(member => hasEmail(member)))
const selectedMembersEmpty = computed(() => selectedMembers.value.length === 0)
const disableInviteButton = computed(() => selectedMembersEmpty.value || !allMembersHaveEmail.value)

const openEditModal = async (member: AssociationMembershipResponse) => {
	await openModal(
		EditMemberModal,
		{ member },
		await modalController.getTop(),
		'Mitglied bearbeiten',
	)
	await refresh()
}

function navigateToSuccess() {
	nav.push(InviteSuccess, { members: selectedMembers.value, nav })
}

const handleInvite = () => handleAsync(async () => {
	await inviteMembers()
	navigateToSuccess()
}, 'Fehler beim Einladen der Mitglieder')
</script>

<template>
	<ModalContent
		title="Mitglieder einladen - Übersicht"
		show-back-button
	>
		<section>
			<ion-item-divider>
				<ion-text v-if="!selectedMembersEmpty">
					<b>Sie haben folgend{{ selectedMembers.length === 1 ? 'es' : 'e' }} Mitglied{{ selectedMembers.length === 1 ? '' : 'er' }} zum Einladen ausgewählt:</b>
				</ion-text>
				<ion-text v-else>
					Es gibt keine Mitglieder zum Einladen.
				</ion-text>
			</ion-item-divider>
		</section>
		<section>
			<MemberList
				group-by="system-user"
				:members="selectedMembers"
			>
				<template #item="member">
					<MemberListItem
						:member="member"
						:description="isSystemUser(member)? 'Bereits zu VCI eingeladen' : undefined"
						:error-text="!hasEmail(member)? 'Bei dem ausgewählten Mitglied ist keine E-Mail Adresse hinterlegt. Wenn Sie das Mitglied zu VCI einladen wollen, dann fügen Sie eine E-Mail Adresse hinzu.' : undefined"
						:disabled="isSystemUser(member)"
						hide-select
					>
						<template #append>
							<ion-button
								v-if="!hasEmail(member)"
								aria-label="Mitglied bearbeiten"
								data-cy="edit-member-button"
								@click="openEditModal(member)"
							>
								<i
									slot="icon-only"
									class="mdi mdi-pencil"
									style="font-size: 20px; color: var(--ion-color-secondary);"
								/>
							</ion-button>
							<ion-button
								v-if="!isSystemUser(member)"
								aria-label="Mitglied aus Liste entfernen"
								@click="toggleSelected(member)"
							>
								<i
									slot="icon-only"
									class="mdi mdi-close-circle-outline"
									style="font-size: 22px; color: var(--ion-color-secondary);"
								/>
							</ion-button>
						</template>
					</MemberListItem>
				</template>
			</MemberList>
		</section>
		<section class="add-member-container">
			<VCIButton
				icon="mdi-plus-thick"
				shape="round"
				size="large"
				aria-label="Weitere Mitglieder hinzufügen"
				@click="nav.pop()"
			/>
			<ion-text>weitere Mitglieder <br> einladen</ion-text>
		</section>

		<template #actions>
			<VCIButton
				icon="mdi-email-plus-outline"
				:disabled="disableInviteButton"
				@click="handleInvite"
			>
				Einladen
			</VCIButton>
		</template>
	</ModalContent>
</template>

<style lang="scss" scoped>
ion-item-divider {
	--background: var(--ion-color-grey-5);
	--color: var(--ion-color-dark);
	--inner-padding-top: 16px;
	--inner-padding-start: 24px;
	--inner-padding-bottom: 16px;
	--padding-start: 0;
	font-size: 15px;
	color: var(--ion-color-dark);
}

.add-member-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: 56px 0;
	font-weight: 600;

	ion-text {
		max-width: 140px;
		margin-top: 8px;
		text-align: center;
	}
}
</style>
